<script lang="ts">
interface Props {
    customerCompanyId?: uuid
}
</script>

<script lang="ts" setup>
import axios from 'axios'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouteLocationRaw, useRoute } from 'vue-router'
import dayjs from 'dayjs'

import { usePaginatedTable } from '@/hooks/table/use-paginated-table'
import { useConfirm } from '@/hooks/use-confirm'
import { useAuthStore } from '@/stores/auth-store'
import { MyDateRangePickerRef, PaginatedResponse, uuid } from '@/types/general'
import { MyButtonScheme } from '@/types/layout/my-button'
import { CompanyDetailsFieldType, Receipt, ReceiptType } from '@/types/pod'
import { GetDataParameters } from '@/types/table'
import { datetime } from '@/utils/dates'
import { useMittListener } from '@/hooks/use-mitt-listener'
import { LicenseType } from '@/types/company'
import { useDownloadConsignmentNotes } from '@/hooks/pod/use-download-consignment-notes'
import { useCompanyStore } from '@/stores/company-store'

import BasicShareModal from '@/components/BasicShareModal.vue'
import ActionMenuItem from '@/components/table/ActionMenuItem.vue'
import ActionRowItem from '@/components/table/ActionRowItem.vue'
import MyFilterButton from '@/components/table/MyFilterButton.vue'
import MyTable from '@/components/table/MyTable.vue'
import MyTableColumn from '@/components/table/MyTableColumn.vue'
import UpdateCompanyDetailsFieldModal from '@/components/packaging/transactions/UpdateCompanyDetailsFieldModal.vue'
import MyFilterDivider from '@/components/table/MyFilterDivider.vue'
// Just a comment to fix a weird eslint bug
import MyFilterSelect from '@/components/table/MyFilterSelect.vue'
import MyButton from '@/components/my-components/MyButton.vue'
import MyDateRangePicker from '@/components/my-components/MyDateRangePicker.vue'

interface FetchReceiptsParams extends Record<string, unknown>, GetDataParameters {
    filter: {
        type: ReceiptType | null
        'customer-company-id'?: uuid
        'start-date': string
        'end-date': string
    }
}

const props = defineProps<Props>()

const { t } = useI18n()
const authStore = useAuthStore()
const confirm = useConfirm()
const route = useRoute()
const { downloading, downloadPDF } = useDownloadConsignmentNotes()
const companyStore = useCompanyStore()

const selectedReceiptId = ref<string>()
const updateFieldModalOpen = ref(false)
const currentField = ref<CompanyDetailsFieldType>('economy')
const shareReceiptOpen = ref(false)
const dateRangePickerRef = ref<MyDateRangePickerRef>()

const { data, paginationData, params, loading, error, refetch } = usePaginatedTable<
    Receipt,
    FetchReceiptsParams
>(
    async (params, abortController) => {
        const response = await axios.get<PaginatedResponse<Receipt>>(
            window.route('pod.company.receipts.index', {
                company: authStore.companyId,
            }),
            { params: params, signal: abortController.signal },
        )

        return response.data
    },
    {
        filter: {
            type: null,
            'customer-company-id': props.customerCompanyId,
            'start-date': dayjs().subtract(6, 'months').startOf('day').toISOString(),
            'end-date': dayjs().endOf('day').toISOString(),
        },
    },
)

const selectedReceipt = computed(() =>
    data.value.find((receipt) => receipt.id === selectedReceiptId.value),
)

const shareEndpoint = computed(() =>
    window.route('pod.company.receipts.share', {
        company: authStore.companyId,
        receipt: selectedReceiptId.value ?? '',
    }),
)

const locations = computed(() =>
    companyStore.locations.map(({ id, name }) => ({ label: name, value: id })),
)

function canDeleteReceipt(row: Receipt) {
    const associatedCompanyId = row.companyId || row.sendingCompany.id || row.haulierCompany?.id
    return associatedCompanyId === authStore.companyId
}

async function deleteReceipt(id: uuid) {
    try {
        await confirm(
            t('deleteEntityTitle', { entity: t('receipt') }),
            t('deleteEntityDescription', { entity: t('receipt') }),
            {
                confirmText: t('yes'),
                cancelText: t('no'),
                confirmButtonScheme: MyButtonScheme.Warning,
            },
        )
    } catch {
        return
    }

    await axios.delete(
        window.route('pod.company.receipts.destroy', { company: authStore.companyId, receipt: id }),
    )
    await refetch()
}

function receiptLink(id: string): RouteLocationRaw {
    if (route.name?.toString()?.includes('customers.show')) {
        return {
            name: 'pod.customers.receipts.show',
            params: { ...route.params, receiptId: id },
            query: route.query,
        }
    }

    return {
        name: 'pod.receipts.show',
        params: { receiptId: id },
        query: route.query,
    }
}

function setFilter(type: string | number | null | undefined, filterName: string) {
    params.value.filter[filterName] = type
    params.value.page = 1
}

function shareReceipt(id: uuid) {
    selectedReceiptId.value = id
    shareReceiptOpen.value = true
}

const fieldValues = computed(() => ({
    economy: selectedReceipt.value?.companyDetails.economy ?? '',
    note1: selectedReceipt.value?.companyDetails.note1 ?? '',
    note2: selectedReceipt.value?.companyDetails.note2 ?? '',
}))

const currentFieldValue = computed(() => fieldValues.value[currentField.value])

function updateField(id: uuid, field: CompanyDetailsFieldType) {
    selectedReceiptId.value = id
    currentField.value = field
    updateFieldModalOpen.value = true
}

useMittListener('fetchReceipts', () => refetch())
</script>

<template>
    <BasicShareModal
        v-if="selectedReceipt"
        v-model="shareReceiptOpen"
        :entity="t('receipt')"
        :endpoint="shareEndpoint"
        :customer-company-id="selectedReceipt.receivingCompany?.id"
        @close="selectedReceiptId = undefined"
    />

    <UpdateCompanyDetailsFieldModal
        v-if="updateFieldModalOpen && selectedReceipt"
        v-model="updateFieldModalOpen"
        :receipt-id="selectedReceipt.id"
        :initial-value="currentFieldValue"
        :field-type="currentField"
    />

    <MyTable
        table-id="pod-receipts"
        :rows="data"
        :pagination-data="paginationData"
        :loading="loading || downloading"
        :error="error"
        :get-data="refetch"
        :entity-name="t('receipts')"
    >
        <template #filters>
            <MyDateRangePicker
                ref="dateRangePickerRef"
                :filter="params.filter"
                @start-date-changed="setFilter($event, 'start-date')"
                @end-date-changed="setFilter($event, 'end-date')"
            />

            <MyFilterDivider />

            <MyFilterButton
                filter-name="type"
                :value="null"
                default
                @selected="setFilter"
                v-text="t('all')"
            />

            <MyFilterButton
                filter-name="type"
                :value="ReceiptType.Dispatch.toString()"
                @selected="setFilter"
                v-text="t('dispatched')"
            />

            <MyFilterButton
                filter-name="type"
                :value="ReceiptType.Delivery.toString()"
                @selected="setFilter"
                v-text="t('delivered')"
            />

            <MyFilterDivider />

            <MyFilterSelect
                :label="t('locations')"
                :options="locations"
                filter-name="location-id"
                @change="setFilter"
            />
        </template>

        <template #actionRow="{ row }">
            <RouterLink :to="receiptLink(row.id)">
                <ActionRowItem>
                    <mdi:eye />
                </ActionRowItem>
            </RouterLink>
        </template>

        <template #actionMenu="{ row }">
            <ActionMenuItem
                class="flex cursor-pointer items-center rounded-lg py-2 px-3 text-sm hover:bg-primary-500 hover:text-primary-50 dark:hover:bg-dark-300"
                @click="shareReceipt(row.id)"
            >
                <mdi:receipt class="mr-2" />
                {{ t('share') }}
            </ActionMenuItem>

            <ActionMenuItem
                v-if="
                    authStore.hasLicense(LicenseType.PodPdfGeneration) &&
                    row.consignmentNotes.length > 0
                "
                class="flex cursor-pointer items-center rounded-lg py-2 px-3 text-sm hover:bg-primary-500 hover:text-primary-50 dark:hover:bg-dark-300"
                @click="downloadPDF(row)"
            >
                <mdi:download class="mr-2" />
                <span
                    v-tooltip="t('downloadConsignmentNotePdf')"
                    class="truncate"
                    v-text="t('downloadConsignmentNotePdf')"
                />
            </ActionMenuItem>

            <ActionMenuItem
                v-if="canDeleteReceipt(row)"
                class="flex cursor-pointer items-center rounded-lg py-2 px-3 text-sm hover:bg-primary-500 hover:text-primary-50 dark:hover:bg-dark-300"
                @click="deleteReceipt(row.id)"
            >
                <mdi:trash-can class="mr-2" />
                {{ t('delete') }}
            </ActionMenuItem>
        </template>

        <MyTableColumn
            :draggable="false"
            :sortable="false"
            name=""
            property="statuses"
            class="justify-start gap-1"
        />

        <template #statuses="{ row }">
            <div class="flex space-x-1 text-xs text-stone-600 dark:text-primary-50">
                <mdi:exclamation-thick
                    v-if="row.hasDeviation"
                    v-tooltip="t('hasDeviation')"
                    class="cursor-help"
                />

                <mdi:file-document
                    v-if="row.consignmentNotes.length > 0"
                    v-tooltip="t('hasConsignmentNotes')"
                    class="cursor-help"
                />

                <mdi:comment v-if="!!row.note" v-tooltip="t('hasNote')" class="cursor-help" />

                <mdi:signature-freehand
                    v-if="!!row.receiverSignature"
                    v-tooltip="t('hasSignature')"
                    class="cursor-help"
                />

                <mdi:image-multiple
                    v-if="row.images.length > 0"
                    v-tooltip="t('hasImages')"
                    class="cursor-help"
                />
            </div>
        </template>

        <MyTableColumn :name="t('type')" property="type" />
        <template #type="{ row }">{{
            t(row.type === ReceiptType.Dispatch ? 'dispatch' : 'delivery')
        }}</template>
        <MyTableColumn :name="t('contractor')" property="company.name" />
        <MyTableColumn
            :name="t('sending', { property: t('location') })"
            property="sendingLocation.name"
        />
        <MyTableColumn :name="t('receiver')" property="receivingCompany.name" :sortable="false" />
        <MyTableColumn
            :name="t('receiving', { property: t('location') })"
            property="receivingLocation.name"
        />
        <MyTableColumn
            :name="t('receiving', { property: t('user') })"
            property="receivingUser.name"
        />
        <template #receivingUser="{ row }">
            {{ row.receivingUser?.name ?? row.receiverName }}
        </template>
        <MyTableColumn :name="t('shipper')" property="shipperCompany.name" :sortable="false" />
        <MyTableColumn :name="t('shipperLocation')" property="shipperLocation.name" />
        <MyTableColumn :name="t('haulier')" property="haulierCompany.name" :sortable="false" />
        <MyTableColumn
            :name="t('haulier', { property: t('location') })"
            property="haulierLocation.name"
        />
        <MyTableColumn :name="t('user')" property="sendingUser.name" />
        <MyTableColumn :name="t('shipmentNumber')" property="shipmentNumber" />
        <MyTableColumn :name="t('trailerNumber')" property="trailerNumber" />
        <MyTableColumn :name="t('truckNumber')" property="truckNumber" />
        <MyTableColumn :name="t('note')" property="note" />
        <MyTableColumn :name="t('transpiredAt')" property="transpiredAt" />
        <template #transpiredAt="{ row }">{{ datetime(row.transpiredAt) }}</template>

        <MyTableColumn :name="t('economy')" property="companyDetails.economy" hidden />

        <template #[`companyDetails.economy`]="{ row }">
            <div class="group/item flex items-center">
                <span class="w-full truncate" v-text="row.companyDetails.economy" />

                <span
                    class="cursor-pointer pl-3 text-stone-600 opacity-0 transition-opacity group-hover/item:opacity-100 dark:text-primary-50"
                    @click="updateField(row.id, 'economy')"
                >
                    <mdi:pencil />
                </span>
            </div>
        </template>

        <MyTableColumn :name="t('note1')" property="companyDetails.note1" hidden />
        <template #[`companyDetails.note1`]="{ row }">
            <div class="group/item flex items-center">
                <span class="w-full truncate" v-text="row.companyDetails.note1" />

                <span
                    v-if="!authStore.isReadonly.pod"
                    class="cursor-pointer pl-3 text-gray-600 opacity-0 transition-opacity group-hover/item:opacity-100 dark:text-primary-50"
                    @click="updateField(row.id, 'note1')"
                >
                    <mdi:pencil />
                </span>
            </div>
        </template>

        <MyTableColumn :name="t('note2')" property="companyDetails.note2" hidden />
        <template #[`companyDetails.note2`]="{ row }">
            <div class="group/item flex items-center">
                <span class="w-full truncate" v-text="row.companyDetails.note2" />

                <span
                    v-if="!authStore.isReadonly.pod"
                    class="cursor-pointer pl-3 text-gray-600 opacity-0 transition-opacity group-hover/item:opacity-100 dark:text-primary-50"
                    @click="updateField(row.id, 'note2')"
                >
                    <mdi:pencil />
                </span>
            </div>
        </template>

        <template #noDataButton>
            <MyButton
                v-if="params.filter['start-date']"
                plain
                scheme="light"
                size="small"
                @click="dateRangePickerRef?.clearDates"
            >
                <mdi:magnify class="mr-2 h-4 w-4" />
                {{ t('searchAllTime') }}
            </MyButton>
        </template>
    </MyTable>
</template>
